import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Order } from '@api/Orders/Order';
import { getOrderLineTotal } from '@utils/Order';
import ProductCategoriesService from '@api/ProductCategories/ProductCategoryCtrl';
import { DataGrid, esES, GridColDef, GridToolbar } from '@mui/x-data-grid';

interface SalesByProductsProps {
    orders: Order[];
}
interface SalesByProduct {
    id: string;
    name: string;
    count: number;
    category: string;
    total: number;
}
const SalesByProducts: React.FunctionComponent<SalesByProductsProps> = (props) => {
    const { orders } = props;
    const productCategoriesService = useMemo(() => new ProductCategoriesService(), []);
    const [products, setProducts] = useState<SalesByProduct[]>([]);
    const [categoryNamesById, setCategoryNamesById] = useState<Map<string, string>>(new Map());

    // Las líneas v2 solo traen productCategoryId (mini-snapshot, sin el doc de
    // categoría embebido de v1) — resolver el nombre contra el catálogo vigente,
    // con el snapshot v1 como fallback para categorías ya borradas.
    useEffect(() => {
        productCategoriesService
            .getProductCategories()
            .then((categories) => {
                const map = new Map<string, string>();
                categories.forEach((category) => category.id && map.set(category.id, category.name));
                setCategoryNamesById(map);
            })
            .catch(() => setCategoryNamesById(new Map()));
    }, []);

    useEffect(() => {
        const array: SalesByProduct[] = [];
        orders.forEach((order) => {
            order.products.forEach((product) => {
                if (product) {
                    // Importe SIEMPRE con getOrderLineTotal (dual v1/v2): line.cost
                    // crudo difiere por origen (web v1 = precio base sin extras)
                    const lineTotal = getOrderLineTotal(product);
                    const index = array.findIndex((p) => p.id === product.product.id);
                    if (index === -1) {
                        array.push({
                            id: product.product.id || '',
                            name: product.product.name,
                            count: product.quantity,
                            category:
                                categoryNamesById.get(product.product.productCategoryId) ||
                                product.product.productCategoryData?.name ||
                                'Sin categoria',
                            total: lineTotal
                        });
                    } else {
                        array[index].count += product.quantity;
                        array[index].total += lineTotal;
                    }
                }
            });
        });
        setProducts(array);
    }, [orders, categoryNamesById]);

    const columns = useMemo<GridColDef<SalesByProduct>[]>(
        () => [
            {
                field: 'name',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Producto
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>,
                valueGetter: ({ row }) => row.name
            },
            {
                field: 'count',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Vendidos
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.count}</Typography>,
                valueGetter: ({ row }) => row.count
            },
            {
                field: 'category',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Cateogría
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.category}</Typography>,
                valueGetter: ({ row }) => row.category
            },
            {
                field: 'total',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.total}`}</Typography>,
                valueGetter: ({ row }) => row.total
            }
        ],
        []
    );

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                sx={{ borderWidth: 0 }}
                rows={products}
                columns={columns}
                slots={{
                    toolbar: GridToolbar
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 }
                    }
                }}
            />
        </Box>
    );
};

export default SalesByProducts;
